import * as React from 'react';
import i18next from 'i18next';
import {
  PanelWrapper,
  Close,
  CloseBtn,
  Header,
  Heading,
  Body,
  Footer,
  UiBlock,
  Wrapper,
} from './wrapper';
import { Portal, PortalNamespace } from '../Portal/index';
import { H1 } from '../Heading/index';
import { IconCancel } from '../Icon/index';
import { FocusTrap } from 'focus-trap-react';
import { useEffect, useId } from 'react';

interface IPanelPortal {
  id?: string | null;
  title: string | null;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

const Panel = ({ id, title, footer, onClose, children }: IPanelPortal) => {
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.focus();
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <Portal id={id || PortalNamespace.DefaultPortal}>
      <FocusTrap focusTrapOptions={{
        clickOutsideDeactivates: true,
        fallbackFocus: () => dialogRef.current as HTMLElement,
      }}
      >
        <Wrapper ref={dialogRef} aria-labelledby={titleId}>
          <UiBlock onClick={onClose} />
          <PanelWrapper>
            <Header>
              <Heading>
                <H1 noMargin id={titleId}>{title}</H1>
              </Heading>
              <Close>
                <CloseBtn aria-label={i18next.t('other.closePanel') || ''} onClick={onClose}>
                  <IconCancel block aria-hidden="true" />
                </CloseBtn>
              </Close>
            </Header>
            <Body>{children}</Body>
            {footer && <Footer>{footer}</Footer>}
          </PanelWrapper>
        </Wrapper>
      </FocusTrap>
    </Portal>
  );
};

export default Panel;
